import type {
	AuthenticationResponseJSON,
	PublicKeyCredentialCreationOptionsJSON,
	PublicKeyCredentialRequestOptionsJSON,
	RegistrationResponseJSON,
} from '@simplewebauthn/browser';
import createDebug from 'debug';
import { getAuthToken } from './client.ts';

const debug = createDebug('app:auth');
const API_BASE = '/api';

// =============================================================================
// Types
// =============================================================================

export interface PasskeyOptionsResponse {
	options?: PublicKeyCredentialRequestOptionsJSON;
	challenge?: string;
	error?: string;
}

export interface LoginResult {
	success: boolean;
	token?: string;
	user?: {
		id: number;
		username: string;
		display_name: string;
		permissions: string[];
	};
	error?: string;
}

export interface CurrentUser {
	id: number;
	username: string;
	display_name: string;
	permissions: string[];
}

export interface InviteValidation {
	valid: boolean;
	user_id?: number;
	username?: string;
	display_name?: string;
	expires_at?: number;
	reason?: 'not_found' | 'used' | 'expired';
}

export interface InviteSetupResult {
	success: boolean;
	token?: string;
	user?: {
		id: number;
		username: string;
		display_name: string;
		email: string;
		permissions: string[];
	};
	error?: string;
}

export interface PasskeyRegistrationOptionsResponse {
	options?: PublicKeyCredentialCreationOptionsJSON;
	error?: string;
}

// =============================================================================
// Passkey Authentication
// =============================================================================

/**
 * Get passkey authentication options from the server
 * POST /api/auth/passkey/options
 *
 * @param username - The username to authenticate (can be empty for conditional UI)
 */
export async function getPasskeyOptions(username: string): Promise<PasskeyOptionsResponse> {
	debug('getPasskeyOptions: username=%s', username || '(empty)');

	try {
		const response = await fetch(`${API_BASE}/auth/passkey/options`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ username }),
		});

		const data = await response.json();
		debug('getPasskeyOptions response: status=%d', response.status);

		if (!response.ok) {
			return {
				error: data.error || `Request failed: ${response.status}`,
			};
		}

		return {
			options: data.options,
			challenge: data.challenge,
		};
	} catch (err) {
		debug('getPasskeyOptions error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { error: message };
	}
}

/**
 * Complete passkey login with credential
 * POST /api/auth/passkey/login
 *
 * @param username - The username
 * @param credential - The WebAuthn credential from startAuthentication
 */
export async function loginWithPasskey(
	username: string,
	credential: AuthenticationResponseJSON
): Promise<LoginResult> {
	debug('loginWithPasskey: username=%s credentialId=%s', username, credential.id);

	try {
		const response = await fetch(`${API_BASE}/auth/passkey/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				username,
				response: credential,
			}),
		});

		const data = await response.json();
		debug('loginWithPasskey response: status=%d', response.status);

		if (!response.ok) {
			return {
				success: false,
				error: data.error || `Login failed: ${response.status}`,
			};
		}

		return {
			success: true,
			token: data.token,
			user: data.user,
		};
	} catch (err) {
		debug('loginWithPasskey error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}

/**
 * Logout the current user
 * POST /api/auth/logout
 */
export async function logout(): Promise<{ success: boolean; error?: string }> {
	debug('logout');

	try {
		const token = getAuthToken();
		const headers: Record<string, string> = {};
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_BASE}/auth/logout`, {
			method: 'POST',
			headers,
		});

		if (!response.ok) {
			const data = await response.json();
			return {
				success: false,
				error: data.error || `Logout failed: ${response.status}`,
			};
		}

		return { success: true };
	} catch (err) {
		debug('logout error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}

/**
 * Get the current authenticated user
 * GET /api/auth/me
 */
export async function getCurrentUser(): Promise<{ user?: CurrentUser; error?: string }> {
	debug('getCurrentUser');

	try {
		const token = getAuthToken();
		const headers: Record<string, string> = {};
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_BASE}/auth/me`, { headers });

		if (response.status === 401) {
			return { error: 'Not authenticated' };
		}

		if (!response.ok) {
			const data = await response.json();
			return { error: data.error || `Request failed: ${response.status}` };
		}

		const data = await response.json();
		return { user: data.user };
	} catch (err) {
		debug('getCurrentUser error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { error: message };
	}
}

// =============================================================================
// Password Authentication
// =============================================================================

/**
 * Login with username and password
 * POST /api/auth/password/login
 *
 * @param username - The username or email
 * @param password - The password
 */
export async function loginWithPassword(username: string, password: string): Promise<LoginResult> {
	debug('loginWithPassword: username=%s', username);

	try {
		const response = await fetch(`${API_BASE}/auth/password/login`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email: username, password }),
		});

		const data = await response.json();
		debug('loginWithPassword response: status=%d', response.status);

		if (!response.ok) {
			if (response.status === 401) {
				return { success: false, error: 'Invalid username or password' };
			}
			if (response.status === 429) {
				return { success: false, error: 'Too many login attempts. Please try again later.' };
			}
			return { success: false, error: data.error || `Login failed: ${response.status}` };
		}

		return {
			success: true,
			token: data.token,
			user: data.user,
		};
	} catch (err) {
		debug('loginWithPassword error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}

// =============================================================================
// Password Management
// =============================================================================

export interface PasswordChangeRequest {
	current_password?: string;
	new_password: string;
}

export interface PasswordChangeResult {
	success: boolean;
	error?: string;
}

/**
 * Change or set user password
 * PUT /api/users/me/password
 *
 * @param newPassword - The new password
 * @param currentPassword - Current password (required if user already has password)
 */
export async function changePassword(
	newPassword: string,
	currentPassword?: string
): Promise<PasswordChangeResult> {
	debug('changePassword: hasCurrentPassword=%s', currentPassword ? 'yes' : 'no');

	try {
		const token = getAuthToken();
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
		};
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const body: PasswordChangeRequest = { new_password: newPassword };
		if (currentPassword) {
			body.current_password = currentPassword;
		}

		const response = await fetch(`${API_BASE}/users/me/password`, {
			method: 'PUT',
			headers,
			body: JSON.stringify(body),
		});

		const data = await response.json();
		debug('changePassword response: status=%d', response.status);

		if (!response.ok) {
			if (response.status === 401) {
				return { success: false, error: 'Current password is incorrect' };
			}
			if (response.status === 400) {
				return { success: false, error: data.error || 'Password must be at least 12 characters' };
			}
			return {
				success: false,
				error: data.error || `Failed to change password: ${response.status}`,
			};
		}

		return { success: true };
	} catch (err) {
		debug('changePassword error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}

/**
 * Delete user password
 * DELETE /api/auth/password
 *
 * Removes the password auth method. Requires at least one passkey to prevent lockout.
 */
export async function deletePassword(): Promise<PasswordChangeResult> {
	debug('deletePassword');

	try {
		const token = getAuthToken();
		const headers: Record<string, string> = {};
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_BASE}/auth/password`, {
			method: 'DELETE',
			headers,
		});

		const data = await response.json();
		debug('deletePassword response: status=%d', response.status);

		if (!response.ok) {
			if (response.status === 400) {
				return {
					success: false,
					error: data.message || 'You must have at least one passkey before removing your password',
				};
			}
			if (response.status === 404) {
				return { success: false, error: 'No password set' };
			}
			return {
				success: false,
				error: data.error || `Failed to delete password: ${response.status}`,
			};
		}

		return { success: true };
	} catch (err) {
		debug('deletePassword error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}

// =============================================================================
// Invite Flow
// =============================================================================

/**
 * Validate an invite token
 * GET /api/auth/invite/:token
 *
 * @param token - The invite token from the URL
 */
export async function validateInvite(token: string): Promise<InviteValidation> {
	debug('validateInvite: token=%s', `${token.substring(0, 8)}...`);

	try {
		const response = await fetch(`${API_BASE}/auth/invite/${encodeURIComponent(token)}`);
		const data = await response.json();
		debug('validateInvite response: status=%d', response.status);

		return data;
	} catch (err) {
		debug('validateInvite error: %o', err);
		return { valid: false, reason: 'not_found' };
	}
}

/**
 * Get passkey registration options for an invite token
 * POST /api/auth/invite/:token/options
 *
 * @param token - The invite token from the URL
 */
export async function getInviteRegistrationOptions(
	token: string
): Promise<PasskeyRegistrationOptionsResponse & { challenge?: string }> {
	debug('getInviteRegistrationOptions: token=%s', `${token.substring(0, 8)}...`);

	try {
		const response = await fetch(`${API_BASE}/auth/invite/${encodeURIComponent(token)}/options`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
		});

		const data = await response.json();
		debug('getInviteRegistrationOptions response: status=%d', response.status);

		if (!response.ok) {
			return {
				error: data.error || `Request failed: ${response.status}`,
			};
		}

		return {
			options: data.options,
			challenge: data.challenge,
		};
	} catch (err) {
		debug('getInviteRegistrationOptions error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { error: message };
	}
}

/**
 * Complete invite setup with passkey registration
 * POST /api/auth/invite/:token/setup
 *
 * @param token - The invite token
 * @param credential - The WebAuthn credential from startRegistration
 * @param challenge - The challenge from the registration options
 * @param deviceName - Optional device name for the passkey
 */
export async function completeInviteSetup(
	token: string,
	credential: RegistrationResponseJSON,
	challenge: string,
	deviceName?: string
): Promise<InviteSetupResult> {
	debug(
		'completeInviteSetup: token=%s credentialId=%s',
		`${token.substring(0, 8)}...`,
		credential.id
	);

	try {
		const response = await fetch(`${API_BASE}/auth/invite/${encodeURIComponent(token)}/setup`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				response: credential,
				challenge,
				device_name: deviceName,
			}),
		});

		const data = await response.json();
		debug('completeInviteSetup response: status=%d', response.status);

		if (!response.ok) {
			// Log full error details to console for debugging
			console.error('[Invite Setup Error]', {
				status: response.status,
				error: data.error,
				details: data.details,
				fullResponse: data,
			});

			return {
				success: false,
				error: data.error || `Setup failed: ${response.status}`,
			};
		}

		return {
			success: true,
			token: data.token,
			user: data.user,
		};
	} catch (err) {
		debug('completeInviteSetup error: %o', err);
		const message = err instanceof Error ? err.message : 'Unknown error';
		return { success: false, error: message };
	}
}
